import { ConnectionType } from '@talos/kyoko';
import { compact } from 'lodash-es';
import {
  getCareOrderMarketsCondition,
  type GetCareOrderMarketsConditionParams,
} from '../conditions/getCareOrderMarketsCondition';
import {
  getMarketAccountActiveCondition,
  type GetMarketAccountActiveConditionParams,
} from '../conditions/getMarketAccountActiveCondition';
import {
  getMarketConfiguredCondition,
  type GetMarketConfiguredConditionParams,
} from '../conditions/useMarketConfiguredCondition';
import type { GetMSSCapabilityEnabledConditionParams } from '../conditions/useMSSCapabilityEnabledCondition';
import { getMSSEnabledCondition, type GetMSSEnabledConditionParams } from '../conditions/useMSSEnabledCondition';
import type { AvailabilityCondition } from '../types';

export type GetRFQMarketSelectorConditionsParams = {
  /** Whether or not to perform MSS validation. Temporary - to be removed in 2.52.0 (upon which it will always be true) */
  enableMSSValidation: boolean;
} & GetCareOrderMarketsConditionParams &
  Omit<GetMarketAccountActiveConditionParams, 'disableIfTradingDisabled'> &
  Omit<GetMarketConfiguredConditionParams, 'connectionType'> &
  Omit<GetMSSCapabilityEnabledConditionParams & GetMSSEnabledConditionParams, 'capability' | 'connectionType'>;

/**
 *  Given the necessary parameters and reference data, will build and return an array of AvailabilityConditions to be used
 * when building MarketSelectorItems for the RFQ use case.
 */
export function getRFQMarketSelectorConditions({
  careOrderMarkets,
  enableMSSValidation,
  symbol,
  marketCredentialsWithConnectionStatusByMarketAccountName,
  mssByAccountBySymbol,
  securitiesBySymbol,
}: GetRFQMarketSelectorConditionsParams): AvailabilityCondition[] {
  const careOrderMarketsCondition = getCareOrderMarketsCondition({ careOrderMarkets });
  const marketConfiguredCondition = getMarketConfiguredCondition({ connectionType: ConnectionType.RFQ });
  const marketAccountActiveCondition = getMarketAccountActiveCondition({
    disableIfTradingDisabled: true,
    marketCredentialsWithConnectionStatusByMarketAccountName,
  });

  const mssEnabledCondition = getMSSEnabledCondition({
    connectionType: ConnectionType.RFQ,
    mssByAccountBySymbol,
    symbol,
    securitiesBySymbol,
  });

  return compact([
    careOrderMarketsCondition,
    marketConfiguredCondition,
    marketAccountActiveCondition,
    // We temporarily disable these conditionaly. Conditionality will be removed in 2.52.0
    enableMSSValidation && mssEnabledCondition,
  ]);
}
